import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, HeadingLevel, AlignmentType, BorderStyle, ImageRun } from 'docx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { useTenant } from '@/contexts/TenantContext';
import meddLogoSrc from '@/assets/medd-logo.png';

/** docx colours are hex WITHOUT the leading '#'. */
function docxColour(hex: string | null | undefined, fallback: string): string {
  const v = String(hex ?? '').trim().replace(/^#/, '');
  return /^[0-9a-f]{6}$/i.test(v) ? v.toUpperCase() : fallback;
}

/** Fetch an image to bytes for embedding. Returns null on any failure so the
 *  document still generates (unbranded) rather than throwing. */
async function fetchImageBytes(url: string | undefined): Promise<Uint8Array | null> {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return new Uint8Array(await res.arrayBuffer());
  } catch {
    return null;
  }
}

export interface DocumentSection {
  id: string;
  name: string;
  type: 'text' | 'list' | 'numbered_list' | 'key_value' | 'table';
  content: any;
}

export interface DocumentContent {
  title: string;
  subtitle?: string;
  generatedAt: string;
  sections: DocumentSection[];
}

export const useDocumentGenerator = () => {
  const { tenant, isMasterTenant } = useTenant();

  // Per-tenant document branding, mirroring usePdfReportGenerator: master MEDD
  // keeps the MEDD logo + navy; white-label tenants (e.g. Boston Scientific) use
  // their own logo and primary colour from TenantContext. Falls back to text +
  // MEDD navy if a logo can't be fetched, so the document always generates.
  const brandName = isMasterTenant ? 'MEDD Sim' : (tenant?.tenant_name || 'Simulation Platform');
  const logoSrc = isMasterTenant ? meddLogoSrc : (tenant?.logo_url || undefined);
  const brandColour = docxColour(isMasterTenant ? undefined : tenant?.primary_color, '233C6F');

  const generateDocx = async (content: DocumentContent, customFilename?: string): Promise<void> => {
    const children: any[] = [];

    // Brand logo header (centred). Silently omitted if the image can't be fetched.
    const logoBytes = await fetchImageBytes(logoSrc);
    if (logoBytes) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
          children: [
            new ImageRun({
              data: logoBytes,
              type: 'png',
              transformation: { width: 170, height: 110 },
            } as any),
          ],
        })
      );
    }

    // Title (brand colour)
    children.push(
      new Paragraph({
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [new TextRun({ text: content.title, bold: true, color: brandColour })],
      })
    );
    
    // Subtitle
    if (content.subtitle) {
      children.push(
        new Paragraph({
          text: content.subtitle,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );
    }
    
    // Sections
    for (const section of content.sections) {
      // Section heading (brand colour)
      children.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 200 },
          children: [new TextRun({ text: section.name, bold: true, color: brandColour })],
        })
      );
      
      // Section content
      switch (section.type) {
        case 'text':
          children.push(
            new Paragraph({
              text: section.content,
              spacing: { after: 200 }
            })
          );
          break;
          
        case 'list': {
          // Guard against non-array content
          const listItems = Array.isArray(section.content) 
            ? section.content 
            : typeof section.content === 'string' 
              ? [section.content] 
              : [];
          listItems.forEach((item: any) => {
            children.push(
              new Paragraph({
                text: typeof item === 'string' ? item : JSON.stringify(item),
                bullet: { level: 0 },
                spacing: { after: 100 }
              })
            );
          });
          break;
        }
          
        case 'numbered_list': {
          // Guard against non-array content
          const numberedItems = Array.isArray(section.content)
            ? section.content
            : typeof section.content === 'string'
              ? [section.content]
              : [];
          numberedItems.forEach((item: any) => {
            children.push(
              new Paragraph({
                text: typeof item === 'string' ? item : JSON.stringify(item),
                numbering: {
                  reference: "default-numbering",
                  level: 0
                },
                spacing: { after: 100 }
              })
            );
          });
          break;
        }
          
        case 'key_value': {
          // Guard against non-array content
          const kvItems = Array.isArray(section.content) ? section.content : [];
          kvItems.forEach((kv: any) => {
            if (kv && typeof kv === 'object' && kv.key !== undefined) {
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: `${kv.key}: `, bold: true }),
                    new TextRun({ text: String(kv.value || '') })
                  ],
                  spacing: { after: 100 }
                })
              );
            }
          });
          break;
        }
          
        case 'table': {
          if (Array.isArray(section.content) && section.content.length > 0) {
            const firstRow = section.content[0];
            if (firstRow && typeof firstRow === 'object') {
              const headers = Object.keys(firstRow);
              const rows = [
                // Header row
                new TableRow({
                  children: headers.map(h => 
                    new TableCell({
                      children: [new Paragraph({ text: h })],
                      shading: { fill: 'E8E8E8' }
                    })
                  )
                }),
                // Data rows
                ...section.content.map((row: any) => 
                  new TableRow({
                    children: headers.map(h =>
                      new TableCell({
                        children: [new Paragraph({ text: String(row?.[h] ?? '') })]
                      })
                    )
                  })
                )
              ];
              
              children.push(
                new Table({
                  rows,
                  width: { size: 100, type: 'pct' as any }
                })
              );
            }
          }
          break;
        }
      }
    }
    
    // Footer
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 400 },
        children: [
          new TextRun({
            text: `Generated by ${brandName} • ${format(new Date(content.generatedAt), 'PPP')}`,
            color: '94A3B8',
            size: 18,
          }),
        ],
      })
    );
    
    const doc = new Document({
      numbering: {
        config: [{
          reference: "default-numbering",
          levels: [{
            level: 0,
            format: "decimal",
            text: "%1.",
            alignment: AlignmentType.START,
          }]
        }]
      },
      sections: [{
        properties: {},
        children
      }]
    });
    
    const blob = await Packer.toBlob(doc);
    const filename = customFilename || `${content.title.replace(/\s+/g, '_')}.docx`;
    saveAs(blob, filename);
  };
  
  return { generateDocx };
};
